import { Database } from "@/integrations/supabase/types";

type ProductVariant = Database['public']['Tables']['product_variants']['Row'];

export interface DisplayVariantInfo {
  variantId: string;
  price: number;
  discountPrice: number | null;
  isDiscounted: boolean;
  stock: number;
  imageUrl: string | null;
  potType: string;
  size: string;
  potColor: string | null;
}

/**
 * Selects the absolute cheapest variant regardless of stock (for Admin use)
 * Stable sort by price, then by variant ID for deterministic output
 */
export const getAbsoluteCheapestVariant = (
  variants: ProductVariant[]
): DisplayVariantInfo | null => {
  if (!variants || variants.length === 0) return null;

  const sortedVariants = [...variants].sort((a, b) => {
    const priceA = a.is_discounted && a.discount_price ? a.discount_price : a.price;
    const priceB = b.is_discounted && b.discount_price ? b.discount_price : b.price;
    
    const priceDiff = Number(priceA) - Number(priceB);
    if (priceDiff !== 0) return priceDiff;
    
    return a.id.localeCompare(b.id);
  });

  const selectedVariant = sortedVariants[0];

  return {
    variantId: selectedVariant.id,
    price: Number(selectedVariant.price),
    discountPrice: selectedVariant.discount_price ? Number(selectedVariant.discount_price) : null,
    isDiscounted: selectedVariant.is_discounted || false,
    stock: selectedVariant.stock,
    imageUrl: selectedVariant.image_url,
    potType: selectedVariant.pot_type,
    size: selectedVariant.size,
    potColor: selectedVariant.pot_color,
  };
};

/**
 * Selects the display variant for customer-facing pages (Shop, Product Cards)
 * 1. Lowest-price variant that is in stock (stock > 0)
 * 2. If all variants are out of stock, pick the absolute cheapest variant
 * 3. Stable sort by price, then by variant ID for deterministic output
 */
export const getDefaultDisplayVariant = (
  variants: ProductVariant[]
): DisplayVariantInfo | null => {
  if (!variants || variants.length === 0) return null;

  // Create a sorted copy: by effective price (discount or regular), then by ID for stability
  const sortedVariants = [...variants].sort((a, b) => {
    const priceA = a.is_discounted && a.discount_price ? a.discount_price : a.price;
    const priceB = b.is_discounted && b.discount_price ? b.discount_price : b.price;
    
    const priceDiff = Number(priceA) - Number(priceB);
    if (priceDiff !== 0) return priceDiff;
    
    // Stable sort by ID when prices are equal
    return a.id.localeCompare(b.id);
  });

  // First, try to find the cheapest in-stock variant
  const inStockVariant = sortedVariants.find(v => v.stock > 0);
  
  // If found, use it; otherwise, use the absolute cheapest (first in sorted list)
  const selectedVariant = inStockVariant || sortedVariants[0];

  return {
    variantId: selectedVariant.id,
    price: Number(selectedVariant.price),
    discountPrice: selectedVariant.discount_price ? Number(selectedVariant.discount_price) : null,
    isDiscounted: selectedVariant.is_discounted || false,
    stock: selectedVariant.stock,
    imageUrl: selectedVariant.image_url,
    potType: selectedVariant.pot_type,
    size: selectedVariant.size,
    potColor: selectedVariant.pot_color,
  };
};
